import TourDetails from '../../../modules/tour/models/entities/TourDetails';
import TourPlanStop from '../../../modules/tour/models/entities/TourPlanStop';
import CustomerTrackingData from '../../../modules/tour/models/types/CustomerTrackingData';

export type ResolvedPlannedStop = {
  stop: TourPlanStop;
  /** 1-basierte Position in der geplanten Reihenfolge (RF-Nummer). */
  stopNumber: number;
  /** Nur fuer den Tooltip. Undefined heisst "Adresse unbekannt", nicht "Stopp verwerfen". */
  customer?: CustomerTrackingData;
};

/**
 * Loest die geplanten Stopps einer Tour fuer die Kartendarstellung auf.
 *
 * Die Geometrie kommt ausschliesslich aus `plannedStops[].lon/.lat` — das ist die Planung von
 * RouteMaster und damit die einzige Quelle dafuer, wo ein geplanter Stopp lag. Frueher lief die
 * Marker-Erzeugung ueber `shipments[0]` -> `tourDetails.deliveryItems` -> `customers`; verfehlte
 * einer dieser drei Lookups, verschwand der Stopp trotz gueltiger Koordinaten von der Karte.
 *
 * Seit dem Java-Cutover ist `tourDetails.deliveryItems` tourweit auf 0-1 Eintraege zusammengefallen
 * (LMAJSM-701), wodurch praktisch die komplette Route wegfiel. Unabhaengig davon bleibt die
 * Kopplung falsch: eine Retoure, eine Paketshop-Sendung oder ein Stopp ohne `empfaengerId`
 * (LMAJSM-673/680) reicht, um einen Stopp aus einer reinen Geometrie-Ansicht zu loeschen.
 *
 * Die Customer-Aufloesung ist deshalb rein additiv und nur noch fuer den Tooltip zustaendig.
 */
export function resolvePlannedStops(tourDetails: TourDetails | null | undefined): ResolvedPlannedStop[] {
  if (!tourDetails?.plannedStops?.length) {
    return [];
  }

  // Fallback-Index deliveryItemId -> customerRef aus den finishedDeliveries. Das ist derselbe
  // Pfad, den lma-api ohnehin nutzt, und er traegt auch dann, wenn das tourweite Manifest leer ist.
  const customerRefByItemId = new Map<string, string>();
  Object.values(tourDetails.finishedDeliveries || {}).forEach((finishedDelivery) =>
    finishedDelivery?.deliveryItems?.forEach((item) => {
      if (item?.deliveryItemId && item?.customerRef) {
        customerRefByItemId.set(item.deliveryItemId, item.customerRef);
      }
    })
  );

  const resolveCustomer = (stop: TourPlanStop): CustomerTrackingData | undefined => {
    const itemIds = [...(stop?.shipments || []), ...(stop?.collectionOrders || [])];
    for (const itemId of itemIds) {
      const customerRef = tourDetails.deliveryItems?.get(itemId)?.customerRef || customerRefByItemId.get(itemId);
      const customer = customerRef ? tourDetails?.customers?.get(customerRef) : undefined;
      if (customer) {
        return customer;
      }
    }
    return undefined;
  };

  // Nach geplanter Reihenfolge sortieren, nicht nach Array-Reihenfolge: Nummerierung und
  // gestrichelte Linie folgen damit der RF-Nummer aus der Planung. Die gespeicherte Reihenfolge
  // ist heute bereits aufsteigend — die Sortierung ist eine Absicherung, keine Korrektur.
  return [...tourDetails.plannedStops]
    .sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0))
    .map((stop, position) => ({
      stop,
      stopNumber: position + 1,
      customer: resolveCustomer(stop),
    }));
}

/**
 * Geplante Stopps, die noch offen sind (LMAJSM-889): keine ihrer Sendungen bzw. Abholauftraege taucht in einer
 * abgeschlossenen Zustellung (Zustellung, Abholung oder erfolgloser Versuch) auf.
 *
 * Hintergrund: die "Offener Stopp"-Pins der Ansicht "Gefahren" kommen aus `tourDetails.customers` ohne
 * abgeschlossene Zustellung. Seit dem Java-Cutover enthaelt das tourweite Manifest nur noch den Kunden des ersten
 * Stopps und waechst mit jedem abgeschlossenen Stopp mit, es gibt also nie einen offenen Kunden. Die Planung von
 * RouteMaster (`plannedStops`) kennt dagegen alle Stopps der Tour; sie dient hier als Ersatzquelle.
 */
export function resolveOpenPlannedStops(tourDetails: TourDetails | null | undefined): ResolvedPlannedStop[] {
  const planned = resolvePlannedStops(tourDetails);
  if (!planned.length) {
    return [];
  }
  const finishedItemIds = new Set<string>();
  Object.values(tourDetails?.finishedDeliveries || {}).forEach((finishedDelivery) =>
    [...(finishedDelivery?.deliveryItems || []), ...(finishedDelivery?.returnDeliveryItems || [])].forEach((item) => {
      if (item?.deliveryItemId) {
        finishedItemIds.add(item.deliveryItemId);
      }
    })
  );
  return planned.filter(({ stop }) => {
    const itemIds = [...(stop?.shipments || []), ...(stop?.collectionOrders || [])];
    return itemIds.length > 0 && !itemIds.some((itemId) => finishedItemIds.has(itemId));
  });
}
